import { DateTime } from 'luxon';

import type * as types from '../types';

import * as constants from '../constants';
import * as terminal from '../terminal';

interface FormatLogMessageParams {
  readonly level: 'unknown' | types.LogLevel;
  readonly message?: string;
  readonly time?: DateTime | number;
}

const BracketPostProcessor = (v: string) => `[${v}]`;

type FormatCallback<T> = (params: FormatLogMessageParams, logger: types.ILoggerConfiguration) => T;

/**
 * Represents instructions for how a given property in the formatted log message should be accessed
 * and formatted inside of the message.
 */
type Formatter = {
  readonly accessor: FormatCallback<null | string>;
  readonly postProcessor?: (formatted: string) => string;
  readonly styles?: FormatCallback<terminal.TerminalStyle> | terminal.TerminalStyle;
};

const Formatters = {
  appInfo: {
    accessor: (_, config) => `${config.appName} v${config.appVersion}`,
    postProcessor: BracketPostProcessor,
    styles: { effect: 'dim', foreground: 'yellow' },
  },
  environmentName: {
    accessor: (_, config) => config.environmentName,
    postProcessor: BracketPostProcessor,
    styles: { effect: 'dim', foreground: 'blue' },
  },
  level: {
    accessor: params => params.level.toUpperCase(),
    postProcessor: BracketPostProcessor,
    styles: params => ({ foreground: constants.LogLevelColors[params.level] }),
  },
  message: {
    accessor: params =>
      params.message && params.message.trim().length !== 0 ? params.message.trim() : null,
    styles: { foreground: 'cyan' },
  },
  name: {
    accessor: (_, config) => config.name,
    postProcessor: BracketPostProcessor,
    styles: { foreground: 'green' },
  },
  time: {
    accessor: (params, config) => {
      let dt: DateTime;
      if (typeof params.time === 'number') {
        dt = DateTime.fromMillis(params.time);
      } else {
        dt = params.time ?? DateTime.local();
      }
      if (dt.isValid) {
        return typeof config.timeFormat === 'string'
          ? dt.toFormat(config.timeFormat)
          : dt.toLocaleString(config.timeFormat);
      }
      return null;
    },
    postProcessor: BracketPostProcessor,
    styles: { foreground: 'blue' },
  },
} as const satisfies Record<string, Formatter>;

const format = (
  formatter: Formatter,
  params: FormatLogMessageParams,
  config: types.ILoggerConfiguration,
) => {
  const value = formatter.accessor(params, config);
  if (value === null) {
    return null;
  }
  let formatted = value;
  if (config.pretty) {
    const styles =
      typeof formatter.styles === 'function' ? formatter.styles(params, config) : formatter.styles;
    if (styles) {
      formatted = terminal.applyStyles(formatted, styles);
    }
  }
  return formatter.postProcessor ? formatter.postProcessor(formatted) : formatted;
};

const joinLogMessage = (elements: (null | string | undefined)[]): string =>
  elements.filter(v => v !== undefined && v !== null && v.trim().length !== 0).join(' ');

/**
 * Constructs a log message string for purposes of emitting a log message to the traditional
 * console object in cases where the {@link Logger} is either configured to log to the browser or
 * cannot use the underlying "pino" logger due to the runtime environment.
 *
 * The formatted message includes certain contextual parameters that are provided to this method
 * as well as configuration parameters that exist on the {@link Logger} instance.
 *
 * @param {FormatLogMessageParams} params
 *   Dynamic parameters that are used to generate the log message string.  These dynamic parameters
 *   are only relevant at the time the log method is called, and are not statically stored on the
 *   logger.
 * @param {LoggerConfiguration} config
 *   Static configuration parameters on the logger that are used to generate the log message string.
 *
 * @returns {string}
 *   The formatted, string message that the logger should emit in cases where it is emitting the
 *   message to the traditional console object, either via the 'browser' configuration on "pino"
 *   or manually - in runtimes where "pino" cannot be used.
 */
export const formatLogMessage = (
  params: FormatLogMessageParams,
  config: types.ILoggerConfiguration,
): string =>
  joinLogMessage([
    joinLogMessage([
      format(Formatters.time, params, config),
      format(Formatters.level, params, config),
      format(Formatters.appInfo, params, config),
      format(Formatters.name, params, config),
      format(Formatters.environmentName, params, config),
    ]) + (params.message && params.message.trim().length !== 0 ? ':' : ''),
    format(Formatters.message, params, config),
  ]);
